import type { FC } from 'react';
import type { ForgotPasswordRequest } from '@/queries/auth/auth.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/Button/button';
import { Input } from '@/components/ui/Input/input';
import Toast from '@/components/ui/Toast/toast';
import { authApi } from '@/queries/auth/auth.api';
import { ForgotPasswordRequestSchema } from '@/queries/auth/auth.schema';
import { StepOfLogin } from '../type';

interface ResetPasswordPopupFormProps {
  setStepOfLogin: (step: StepOfLogin) => void;
  setEmail: (email: string) => void;
  initialEmail?: string;
}

export const ResetPasswordPopupForm: FC<ResetPasswordPopupFormProps> = ({ setStepOfLogin, setEmail, initialEmail = '' }) => {
  const t = useTranslations('reset-password');
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    watch,
  } = useForm<ForgotPasswordRequest>({
    resolver: zodResolver(ForgotPasswordRequestSchema),
    defaultValues: {
      email: initialEmail,
    },
  });

  const email = watch('email');

  const isFormValid = useMemo(() => {
    return email.trim() !== '';
  }, [email]);

  const onSubmit = async (data: ForgotPasswordRequest) => {
    try {
      await authApi.forgotPassword(data);

      setEmail(data.email);
      setStepOfLogin(StepOfLogin.SUCCESS_RESET);
    } catch (error) {
      console.error(error);
      Toast({
        type: 'error',
        title: t('incorrect-email-or-password'),
      });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="w-full flex h-full flex-col justify-between gap-y-6">
        <div className="lg:min-h-97 mt-3.25">
          <Input
            {...register('email')}
            id="email"
            type="email"
            label={t('email')}
            placeholder={t('email-placeholder')}
            labelClassName="text-white paragraph-s-regular"
            error={{
              isError: !!errors?.email,
              errorText: errors?.email?.message || '',
            }}
          />
          <p className="text-c300 paragraph-s-regular mt-4 whitespace-pre-line">
            {t.rich('send-email-letter', {
              br: () => <br />,
            })}
          </p>
        </div>

        <Button
          type="submit"
          className="w-full py-3 mt-auto"
          disabled={!isFormValid || isSubmitting}
          isLoading={isSubmitting}
        >
          {t('reset')}
        </Button>
      </form>
    </>
  );
};
